import { useEffect } from "react";
import { Controller } from "react-hook-form";
import {
  Dropdown,
  DropdownButton,
  DropdownBody,
  DropdownItem,
} from "@components";
import { cn, LocalErrorBoundary } from "@utils";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

const StyledSelect = ({
  name,
  control,
  options,
  defaultSelected,
  className,
  required,
  displayName,
  rules = undefined,
  renderOptions = null,
  renderDisplay = null,
  width = null,
}) => {
  const defaultValue = defaultSelected || options[0]?.key;

  return (
    <LocalErrorBoundary>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={
          rules
            ? rules
            : {
                required: required ? `${displayName} is required` : null,
              }
        }
        render={({ field: { value, onChange, onBlur } }) => {
          const targetOption = options.find((option) => option.key === value);

          useEffect(() => {
            if (targetOption && targetOption.disabled) {
              const nextAvailableOption = options.find(
                (option) => !option.disabled
              );
              const newValue = nextAvailableOption
                ? nextAvailableOption.key
                : null;
              onChange(newValue);
            }
          }, [value, options, targetOption, onChange]);

          const selectedOption =
            options.find((option) => option.key === value) || null;

          return (
            <div className="relative">
              <Dropdown className={className}>
                {({ open }) => (
                  <>
                    <DropdownButton
                      className={cn(
                        "w-full appearance-none rounded-md bg-white py-2 pl-3 pr-12 text-sm text-zinc-900 cursor-pointer border",
                        open ? "border-primary" : "border-zinc-300"
                      )}
                    >
                      <div className="w-full truncate">
                        {selectedOption ? (
                          renderDisplay ? (
                            renderDisplay(selectedOption.label)
                          ) : (
                            <span>{selectedOption.label}</span>
                          )
                        ) : (
                          <span className="text-zinc-500/70">
                            {displayName}
                          </span>
                        )}
                      </div>
                      <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                    </DropdownButton>

                    <DropdownBody className={cn(width ? `w-${width}` : "w-80")}>
                      {options.map((option) => {
                        const isSelected =
                          option.key === value ||
                          (!value && option.key === defaultValue);
                        return (
                          <DropdownItem
                            key={option.key || option.value}
                            onClick={() => {
                              onChange(option.key);
                            }}
                            closeOnComplete
                            disabled={option.disabled}
                            className="rounded-lg"
                          >
                            <div
                              className={cn(
                                "w-full select-none",
                                isSelected
                                  ? "text-primary-500"
                                  : "text-zinc-900",
                                option.disabled && "text-zinc-500"
                              )}
                            >
                              {renderOptions
                                ? renderOptions(
                                    option.label,
                                    isSelected,
                                    option.disabled
                                  )
                                : option.label}
                            </div>
                          </DropdownItem>
                        );
                      })}
                    </DropdownBody>
                  </>
                )}
              </Dropdown>
            </div>
          );
        }}
      />
    </LocalErrorBoundary>
  );
};

export default StyledSelect;
